import { isAllowedImageType, MAX_HEIC_CONVERSION_BYTES } from "@shared/products/photo-constraints";
import { reencodeInBrowser } from "./storable-format";
import { PhotoValidationError } from "./validation";

export const HEIC_CONVERSION_PATH = "/api/photos/heic";
const SERVER_CONVERSION_TIMEOUT_MS = 60_000;

function isHeicFile(file: File): boolean {
  if (file.type === "image/heic" || file.type === "image/heif") return true;
  return !isAllowedImageType(file.type) && /\.hei[cf]$/i.test(file.name);
}

async function requestServerConversion(file: File): Promise<Response | null> {
  try {
    return await fetch(HEIC_CONVERSION_PATH, {
      method: "POST",
      headers: { "Content-Type": "application/octet-stream" },
      body: file,
      signal: AbortSignal.timeout(SERVER_CONVERSION_TIMEOUT_MS),
    });
  } catch {
    return null;
  }
}

function tooLargeError(): PhotoValidationError {
  return new PhotoValidationError("This HEIC photo is over 20 MB. Save or export it as JPEG and try again.");
}

async function convertOnServer(file: File, nativeError: unknown): Promise<File> {
  if (file.size > MAX_HEIC_CONVERSION_BYTES) throw tooLargeError();
  const response = await requestServerConversion(file);
  if (response?.status === 413) throw tooLargeError();
  if (response?.status === 429) {
    throw new PhotoValidationError("Too many photos at once. Wait a minute and try again.");
  }
  if (!response?.ok) throw nativeError;
  const converted = await response.blob();
  if (converted.type !== "image/webp" || converted.size === 0) throw nativeError;
  const newName = file.name.replace(/\.[^./\\]+$/, "") + ".webp";
  return new File([converted], newName, { type: "image/webp" });
}

export async function convertHeicIfNeeded(file: File): Promise<File> {
  if (!isHeicFile(file)) {
    return file;
  }

  try {
    return await reencodeInBrowser(file);
  } catch (nativeError) {
    return await convertOnServer(file, nativeError);
  }
}
